import myImageLoader from '@/image.local.loader';
import Image from 'next/image';
import {
    ChevronLeftWhite,
    ChevronRightWhite,
    ExpandImage,
    ExtendImage,
    imgPlaceholder,
} from 'public/Images';
import { useEffect, useState } from 'react';
import Typography from '../typography/typography.component';
import { useTranslation } from '@/hook';

interface ImprefectionsGalleryProps {
    images: string[];
    disableGallery: boolean;
}

const ImprefectionsGallery: React.FC<ImprefectionsGalleryProps> = ({
    images,
    disableGallery,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);

    const showPrev = () => {
        if (currentIndex > 0) {
            setIsLoading(true);
            setCurrentIndex(currentIndex - 1);
        }
    };
    const showNext = () => {
        if (currentIndex < images.length - 1) {
            setIsLoading(true);
            setCurrentIndex(currentIndex + 1);
        }
    };
    useEffect(() => {
        if (disableGallery) {
            setCurrentIndex(0);
        }
    }, [disableGallery]);
    const handleImageLoad = () => {
        setTimeout(() => {
            setIsLoading(false);
        }, 300);

        // Set loading to false when the image is loaded
    };
    return (
        <div
            className={`relative w-full max-w-xl mx-auto ${
                disableGallery ? 'disable-gallery' : ''
            }`}
        >
            <div className="w-full md:w-80 h-52 disable-gallery:w-16 disable-gallery:h-11 transition-all duration-500 rounded-lg disable-gallery:rounded">
                {isLoading && (
                    <div className="absolute  w-full h-full bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                        <Image
                            src={imgPlaceholder}
                            alt="placeholder"
                            className="disable-gallery:md:w-12 disable-gallery:md:h-10"
                        />
                    </div>
                )}
                <div className="w-full md:w-80 h-52 disable-gallery:w-16 disable-gallery:h-11 transition-all duration-500 rounded-lg disable-gallery:rounded">
                    <img
                        src={myImageLoader({
                            src: images[currentIndex],
                            width: 320,
                            height: 200,
                            quality: 80,
                        })}
                        alt={`Image ${currentIndex + 1}`}
                        className="disable-gallery:w-full w-[330px] md:w-full disable-gallery:h-11 h-52  rounded-lg disable-gallery:rounded transition-[height] duration-500"
                        onLoad={handleImageLoad}
                    />
                </div>
                {currentIndex > 0 && (
                    <div
                        className="disable-gallery:hidden absolute top-1/2 left-2 transform -translate-y-1/2 bg-white bg-opacity-[0.32] text-white p-2 flex items-center justify-center w-9 h-9 rounded-full cursor-pointer"
                        onClick={e => {
                            e.stopPropagation();
                            showPrev();
                        }}
                    >
                        <Image src={ChevronLeftWhite} alt="left-arrow" />
                    </div>
                )}
                {currentIndex < images.length - 1 && (
                    <div
                        className="disable-gallery:hidden absolute top-1/2 right-2 transform -translate-y-1/2 bg-white bg-opacity-[0.32] text-white p-2 flex items-center justify-center w-9 h-9 rounded-full cursor-pointer"
                        onClick={e => {
                            e.stopPropagation();
                            showNext();
                        }}
                    >
                        <Image src={ChevronRightWhite} alt="left-arrow" />
                    </div>
                )}
            </div>
            {
                <>
                    <div
                        className="disable-gallery:hidden w-max flex items-center justify-center absolute bottom-2 right-2 bg-black bg-opacity-[0.32] py-0.5 px-1 rounded ar:hidden"
                        dir="ltr"
                    >
                        <Typography variant="caption" className="!text-white">
                            {currentIndex + 1} {t('of')} {images.length}
                        </Typography>
                    </div>

                    <div
                        className="disable-gallery:hidden w-max flex items-center justify-center absolute bottom-2 right-2 bg-black bg-opacity-[0.32] py-0.5 px-1 rounded en:hidden"
                        dir="rtl"
                    >
                        <Typography variant="caption" className="!text-white">
                            {currentIndex + 1} {t('of')} {images.length}
                        </Typography>
                    </div>
                </>
            }
        </div>
    );
};

const ImprefectionReportImageGallery = ({
    images,
    getDisableGalleryState,
}: {
    images: string[];

    getDisableGalleryState: (disableGallery) => void;
}) => {
    const [disableGallery, setDisableGallery] = useState(true);
    useEffect(() => {
        getDisableGalleryState(disableGallery);
    }, [disableGallery]);

    return (
        <div className={`${disableGallery ? 'disable-gallery' : ''}`}>
            <div
                className="relative disable-gallery:cursor-pointer w-auto h-auto en:hidden"
                onClick={() => {
                    disableGallery && setDisableGallery(false);
                }}
                dir={'ltr'}
            >
                <ImprefectionsGallery
                    images={images}
                    disableGallery={disableGallery}
                />
                <Image
                    className={`absolute !cursor-pointer disable-gallery:top-1/2 en:disable-gallery:left-1/2 ar:disable-gallery:right-1/2 en:disable-gallery:-translate-x-1/2 ar:disable-gallery:translate-x-1/2 disable-gallery:-translate-y-1/2 top-2.5 en:right-2.5 ar:left-2.5 translate-x-0 translate-y-0
                    }`}
                    src={disableGallery ? ExpandImage : ExtendImage}
                    alt="expand-image"
                    onClick={() => {
                        !disableGallery && setDisableGallery(true);
                    }}
                />
            </div>
            <div
                className="relative disable-gallery:cursor-pointer w-auto h-auto ar:hidden"
                onClick={() => {
                    disableGallery && setDisableGallery(false);
                }}
                dir={'rtl'}
            >
                <ImprefectionsGallery
                    images={images}
                    disableGallery={disableGallery}
                />
                <Image
                    className={`absolute !cursor-pointer disable-gallery:top-1/2 en:disable-gallery:left-1/2 ar:disable-gallery:right-1/2 en:disable-gallery:-translate-x-1/2 ar:disable-gallery:translate-x-1/2 disable-gallery:-translate-y-1/2 top-2.5 en:right-2.5 ar:left-2.5 translate-x-0 translate-y-0
                    }`}
                    src={disableGallery ? ExpandImage : ExtendImage}
                    alt="expand-image"
                    onClick={() => {
                        !disableGallery && setDisableGallery(true);
                    }}
                />
            </div>
        </div>
    );
};
export default ImprefectionReportImageGallery;
